import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { getExam, createAttempt } from '../api/exams';
import { SpinnerOverlay } from '../components/index.jsx';
import TopNav from '../components/TopNav.jsx';
import { useAttempt } from '../context/AttemptContext';
import { useAuth } from '../context/AuthContext';

const CHECKOUT_RETURN_TO_KEY = 'checkout_return_to';

export default function FullExam() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { startFullExam } = useAttempt();
  const { user } = useAuth();
  const [exam, setExam] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280));
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  const isMobile = viewportWidth <= 768;
  const isSmallMobile = viewportWidth <= 480;

  useEffect(() => {
    getExam(examId).then(setExam).catch(console.error);
  }, [examId]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const targetDate = new Date('2026-06-14T00:00:00');
    const updateCountdown = () => {
      const now = new Date();
      const diff = targetDate - now;
      if (diff > 0) {
        setCountdown({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000),
        });
      } else {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (exam?.exam_family !== 'fet') return undefined;

    window.sessionStorage.setItem(CHECKOUT_RETURN_TO_KEY, `/exam/${examId}`);

    const lockedPath = `/exam/${examId}`;
    window.history.pushState({ fetLandingLock: true }, '', lockedPath);

    const handlePopState = () => {
      navigate(lockedPath, { replace: true });
      window.history.pushState({ fetLandingLock: true }, '', lockedPath);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [exam?.exam_family, examId, navigate]);

  const handleStart = async () => {
    try {
      if (exam?.exam_family === 'fet') {
        navigate(`/writing/${examId}?sharedFet=true`);
        return;
      }
      const attempt = await createAttempt(examId, 'full_exam', 'full_exam');
      startFullExam(attempt.id, examId);
      navigate(`/writing/${examId}?fullExam=true`);
    } catch (err) {
      console.error(err);
      alert('Could not start exam. Please try again.');
    }
  };

  if (!exam) return <><TopNav backTo={`/hub/${examId}`} backLabel="Back" /><SpinnerOverlay /></>;
  if (exam.exam_family !== 'fet' && !user?.capabilities?.full_exam) {
    return <Navigate to={`/pricing?locked=full_exam&return_to=${encodeURIComponent(`/exam/${examId}`)}`} replace />;
  }
  if (exam.exam_family === 'fet') {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #ffffff 0%, #eef2ff 100%)',
        fontFamily: "'Outfit', sans-serif",
        overflow: 'hidden',
        position: 'relative',
        color: '#111827'
      }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Space+Mono:wght@400;700&display=swap');
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-20px) rotate(2deg); }
          }
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(40px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes glow {
            0%, 100% { box-shadow: 0 0 20px rgba(79, 70, 229, 0.22); }
            50% { box-shadow: 0 0 40px rgba(79, 70, 229, 0.34); }
          }
          @keyframes countdown-tick {
            0%, 100% { transform: scale(1); }
            10% { transform: scale(1.1); }
          }
          .countdown-number { animation: countdown-tick 1s ease-in-out infinite; }
          .hero-text { animation: slideUp 0.8s ease-out forwards; }
          .cta-button {
            animation: glow 2s ease-in-out infinite;
            transition: all 0.3s ease;
          }
          .cta-button:hover {
            transform: translateY(-4px) scale(1.02);
            box-shadow: 0 20px 40px rgba(79, 70, 229, 0.28);
          }
          .feature-card {
            transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          }
          .feature-card:hover {
            transform: translateY(-8px);
          }
          .floating-shape {
            animation: float 6s ease-in-out infinite;
          }
        `}</style>

        <TopNav containerWidth="1200px" />

        {!isMobile && <div className="floating-shape" style={{
          position: 'absolute',
          top: '10%',
          right: '10%',
          width: '300px',
          height: '300px',
          background: 'linear-gradient(135deg, rgba(199,210,254,.35) 0%, rgba(139,92,246,.05) 100%)',
          borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
          filter: 'blur(1px)',
          zIndex: 0
        }} />}

        {!isMobile && <div className="floating-shape" style={{
          position: 'absolute',
          bottom: '15%',
          left: '5%',
          width: '200px',
          height: '200px',
          background: 'linear-gradient(135deg, rgba(16,185,129,.05) 0%, rgba(199,210,254,.22) 100%)',
          borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
          filter: 'blur(1px)',
          animationDelay: '-3s',
          zIndex: 0
        }} />}

        <div style={{
          position: 'relative',
          zIndex: 1,
          maxWidth: '1200px',
          margin: '0 auto',
          padding: isMobile ? '24px 16px 36px' : '40px 24px',
          minHeight: 'calc(100vh - 68px)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div className="hero-text" style={{ marginBottom: '20px', animationDelay: '0.1s' }}>
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              letterSpacing: isMobile ? '2px' : '3px',
              textTransform: 'uppercase',
              background: 'linear-gradient(135deg, #4F46E5 0%, #7c3aed 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>FET EXAM PREP</span>
          </div>

          <h1 className="hero-text" style={{
            fontSize: 'clamp(42px, 8vw, 80px)',
            fontWeight: '800',
            textAlign: 'center',
            color: '#111827',
            lineHeight: '1.1',
            marginBottom: '24px',
            animationDelay: '0.2s',
            letterSpacing: '-2px'
          }}>
            The Clock Is<br />
            <span style={{
              background: 'linear-gradient(135deg, #4F46E5 0%, #7c3aed 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>Ticking</span>
          </h1>

          <p className="hero-text" style={{
            fontSize: '20px',
            color: '#374151',
            textAlign: 'center',
            maxWidth: '500px',
            marginBottom: '48px',
            lineHeight: '1.6',
            animationDelay: '0.3s'
          }}>
            Your FET exam is approaching. Start preparing now with AI-powered writing practice and instant feedback.
          </p>

          <div className="hero-text" style={{
            display: 'flex',
            gap: isSmallMobile ? '10px' : '16px',
            marginBottom: isMobile ? '36px' : '56px',
            animationDelay: '0.4s',
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            {[
              { value: countdown.days, label: 'Days' },
              { value: countdown.hours, label: 'Hours' },
              { value: countdown.minutes, label: 'Minutes' },
              { value: countdown.seconds, label: 'Seconds' }
            ].map((item, i) => (
              <div key={item.label} style={{
                background: '#ffffff',
                borderRadius: '16px',
                padding: isSmallMobile ? '16px 18px' : isMobile ? '18px 22px' : '24px 32px',
                minWidth: isSmallMobile ? '78px' : isMobile ? '88px' : '100px',
                textAlign: 'center',
                boxShadow: '0 10px 30px rgba(15,23,42,.06)',
                border: '1px solid #E5E7EB'
              }}>
                <div className="countdown-number" style={{
                  fontFamily: "'Space Mono', monospace",
                  fontSize: isSmallMobile ? '28px' : isMobile ? '34px' : '48px',
                  fontWeight: '700',
                  color: '#111827',
                  lineHeight: '1',
                  animationDelay: `${i * 0.1}s`
                }}>
                  {item.value.toString().padStart(2, '0')}
                </div>
                <div style={{
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#9CA3AF',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  marginTop: '8px'
                }}>{item.label}</div>
              </div>
            ))}
          </div>

          <div className="hero-text" style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px',
            width: '100%',
            maxWidth: '900px',
            marginBottom: '48px',
            animationDelay: '0.5s'
          }}>
            <div className="feature-card" style={{
              background: '#ffffff',
              borderRadius: '20px',
              padding: isMobile ? '24px' : '32px',
              boxShadow: '0 10px 30px rgba(15,23,42,.06)',
              border: '2px solid #4F46E5',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer'
            }} onClick={handleStart}>
              <div style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#fff',
                fontSize: '11px',
                fontWeight: '700',
                padding: '6px 12px',
                borderRadius: '20px',
                letterSpacing: '0.5px'
              }}>AVAILABLE NOW</div>

              <div style={{
                width: '56px',
                height: '56px',
                background: '#4F46E5',
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px'
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 19l7-7 3 3-7 7-3-3z"/>
                  <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
                  <path d="M2 2l7.586 7.586"/>
                </svg>
              </div>

              <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '12px' }}>
                Writing Practice
              </h3>

              <p style={{ fontSize: '15px', color: '#374151', lineHeight: '1.6', marginBottom: '20px' }}>
                Practice emails and essays with instant AI marking. Get detailed feedback on grammar, structure, and content.
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#4F46E5', fontWeight: '600', fontSize: '15px' }}>
                Start Practicing
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </div>
            </div>

            <div className="feature-card" style={{
              background: '#ffffff',
              borderRadius: '20px',
              padding: isMobile ? '24px' : '32px',
              boxShadow: '0 10px 30px rgba(15,23,42,.06)',
              border: '1px solid #E5E7EB',
              position: 'relative',
              opacity: 0.7
            }}>
              <div style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: '#F8FAFC',
                color: '#4B5563',
                fontSize: '11px',
                fontWeight: '700',
                padding: '6px 12px',
                borderRadius: '20px',
                letterSpacing: '0.5px'
              }}>COMING SOON</div>

              <div style={{
                width: '56px',
                height: '56px',
                background: '#F1F5F9',
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px'
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                </svg>
              </div>

              <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#94A3B8', marginBottom: '12px' }}>
                Reading Practice
              </h3>

              <p style={{ fontSize: '15px', color: '#94A3B8', lineHeight: '1.6' }}>
                Full reading comprehension practice with timed tests and detailed explanations.
              </p>
            </div>

            <div className="feature-card" style={{
              background: '#ffffff',
              borderRadius: '20px',
              padding: isMobile ? '24px' : '32px',
              boxShadow: '0 10px 30px rgba(15,23,42,.06)',
              border: '1px solid #E5E7EB',
              position: 'relative',
              opacity: 0.7
            }}>
              <div style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: '#F8FAFC',
                color: '#4B5563',
                fontSize: '11px',
                fontWeight: '700',
                padding: '6px 12px',
                borderRadius: '20px',
                letterSpacing: '0.5px'
              }}>COMING SOON</div>

              <div style={{
                width: '56px',
                height: '56px',
                background: '#F1F5F9',
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px'
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
                  <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
                </svg>
              </div>

              <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#94A3B8', marginBottom: '12px' }}>
                Listening Practice
              </h3>

              <p style={{ fontSize: '15px', color: '#94A3B8', lineHeight: '1.6' }}>
                Audio-based exercises with real exam scenarios and instant transcription feedback.
              </p>
            </div>
          </div>

          <button
            className="cta-button"
            onClick={handleStart}
            style={{
              background: '#4F46E5',
              color: '#ffffff',
              fontSize: isMobile ? '16px' : '18px',
              fontWeight: '600',
              padding: isMobile ? '18px 24px' : '20px 48px',
              borderRadius: '14px',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              width: isMobile ? '100%' : 'auto',
              justifyContent: 'center',
              maxWidth: isMobile ? '360px' : 'none'
            }}
          >
            Start Writing Practice
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>

          <p style={{ marginTop: '32px', fontSize: '14px', color: '#9CA3AF' }}>
            Same AI backend • Sign in required for marked practice
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <TopNav backTo={exam.exam_family === 'fet' ? '/platform' : `/hub/${examId}`} backLabel="Back" />
      <div style={{ maxWidth: 640, margin: '60px auto', padding: 20, textAlign: 'center' }}>
        <div className="card card-body">
          <>
              <div style={{
                width: 72, height: 72,
                background: 'linear-gradient(135deg,var(--ac),#fbbf24)',
                borderRadius: 18, display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 32,
                margin: '0 auto 20px',
                boxShadow: '0 8px 24px rgba(217,119,6,.3)'
              }}>🏆</div>

              <h2 style={{ fontSize: 28, marginBottom: 10 }}>Full Mock Exam</h2>
              <p style={{ fontSize: 14, color: 'var(--mu)', lineHeight: 1.7, marginBottom: 28 }}>
                Complete all three sections back-to-back.<br />
                A comprehensive results report is generated at the end.
              </p>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 28 }}>
                {[
                  ['✍️', 'Writing', `${exam.time_mins || 45} min`, 'var(--sk)'],
                  ['🎙', 'Speaking', '~15 min', 'var(--tl)'],
                  ['📖', 'Reading', '45 min', 'var(--pu)'],
                ].map(([ic, lb, tm, cl], i, arr) => (
                  <React.Fragment key={lb}>
                    <div style={{ border: `2px solid ${cl}`, borderRadius: 10, padding: '14px 16px', textAlign: 'center', minWidth: 120, background: '#fff' }}>
                      <div style={{ fontSize: 22, marginBottom: 5 }}>{ic}</div>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{lb}</div>
                      <div style={{ fontSize: 11, color: 'var(--mu)', marginTop: 2 }}>{tm}</div>
                    </div>
                    {i < arr.length - 1 && <div style={{ fontSize: 18, color: 'var(--bo2)' }}>→</div>}
                  </React.Fragment>
                ))}
              </div>

              <div style={{
                background: '#fffbeb', border: '1px solid #fde68a',
                borderRadius: 10, padding: 14, marginBottom: 22,
                fontSize: 13, color: '#92400e',
                textAlign: 'right', fontFamily: 'Cairo, sans-serif', direction: 'rtl'
              }}>
                <strong>تعليمات:</strong> أكمل كل قسم بالترتيب. اكتب بشكل طبيعي كما ستفعل في الاختبار الحقيقي. لا تتوقف بين الأقسام.
              </div>

              <button className="btn btn-gold btn-lg btn-full" onClick={handleStart}>
                Start Full Exam — Begin Writing ✍️
              </button>
          </>
        </div>
      </div>
    </div>
  );
}
